import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from '@/lib/crm/hooks/useEntityTypeSchema';

export interface AssociationTypeValueLabelProps {
  value: string;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  truncate?: boolean;
}

const fontSizeClasses: Record<string, string> = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Display component for association_type field values.
 *
 * Resolves an association type name (e.g., "ahoy_contact_to_company") to a
 * human-readable label by combining the source entity type label with the
 * lowercased association label: "Contact works at".
 *
 * Falls back to the raw value while loading or if lookup fails.
 */
export const AssociationTypeValueLabel: React.FC<
  AssociationTypeValueLabelProps
> = ({ value, fontSize = 'sm', className = '', truncate }) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const { associationTypes } = useAssociationTypes(orgId);

  const { data: entityTypes = [] } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId ?? 0),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const displayLabel = useMemo(() => {
    const assocType = associationTypes.find(
      a => a.association_type_name === value
    );
    if (!assocType) {
      return value;
    }

    const sourceEntityType = entityTypes.find(
      e => e.entity_type_name === assocType.source_entity_type_name
    );

    const sourceLabel =
      sourceEntityType?.label ?? assocType.source_entity_type_name;
    return `${sourceLabel} ${assocType.label.toLowerCase()}`;
  }, [value, associationTypes, entityTypes]);

  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${className}`}
      title={truncate ? displayLabel : undefined}
    >
      {displayLabel}
    </span>
  );
};
